import React from 'react';

/**
 * A reusable hook for handling animations with IntersectionObserver
 * 
 * @param {Object} options Animation options
 * @param {boolean} options.enableAnimations Whether animations are enabled
 * @param {string} options.animationType The type of animation (fade-in, slide-up, etc)
 * @param {number} options.animationDuration Duration of the animation in ms
 * @param {number} options.animationDelay Delay before animation starts in ms
 * @param {string} options.animationTrigger When to trigger the animation (onload, onscroll)
 * @returns {Object} Animation properties and ref
 */
export const useAnimation = ({
  enableAnimations = false,
  animationType = 'fade-in',
  animationDuration = 800,
  animationDelay = 0,
  animationTrigger = 'onscroll'
}) => {
  const [isVisible, setIsVisible] = React.useState(false);
  const elementRef = React.useRef(null);

  React.useEffect(() => {
    if (!enableAnimations) {
      setIsVisible(true);
      return;
    }

    if (animationTrigger === 'onload') {
      // Animate on page load with delay
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, animationDelay);
      return () => clearTimeout(timer);
    } else if (animationTrigger === 'onscroll') {
      // Set up intersection observer for scroll-based animations
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              setIsVisible(true);
            }, animationDelay);
            
            // Once animated, no need to observe anymore
            if (elementRef.current) {
              observer.unobserve(elementRef.current);
            }
          }
        },
        { threshold: 0.1 } // Trigger when at least 10% of the element is visible
      );
      
      if (elementRef.current) {
        observer.observe(elementRef.current);
      }
      
      return () => {
        if (elementRef.current) {
          observer.unobserve(elementRef.current);
        }
      };
    }
  }, [enableAnimations, animationTrigger, animationDelay]);

  // Generate animation style
  const getAnimationStyle = () => {
    if (!enableAnimations) return {};

    const baseStyle = {
      opacity: isVisible ? 1 : 0,
      transition: `opacity ${animationDuration}ms ease, transform ${animationDuration}ms ease`,
    };

    let transformStyle = {};

    switch (animationType) {
      case 'fade-in':
        // Just opacity which is already in baseStyle
        break;
      case 'slide-up':
        transformStyle = {
          transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
        };
        break;
      case 'slide-down':
        transformStyle = {
          transform: isVisible ? 'translateY(0)' : 'translateY(-30px)',
        };
        break;
      case 'slide-left':
        transformStyle = {
          transform: isVisible ? 'translateX(0)' : 'translateX(30px)',
        };
        break;
      case 'slide-right':
        transformStyle = {
          transform: isVisible ? 'translateX(0)' : 'translateX(-30px)',
        };
        break;
      case 'slide-fade-up':
        transformStyle = {
          transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
        };
        break;
      case 'zoom-in':
        transformStyle = {
          transform: isVisible ? 'scale(1)' : 'scale(0.95)',
        };
        break;
      case 'zoom-out':
        transformStyle = {
          transform: isVisible ? 'scale(1)' : 'scale(1.05)',
        };
        break;
      default:
        break;
    }

    return { ...baseStyle, ...transformStyle };
  };

  return {
    ref: elementRef,
    isVisible,
    style: getAnimationStyle(),
  };
};

export default useAnimation;